"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { Repeat } from "lucide-react";
import { cn } from "@/lib/utils";
import { translateSupportMessage } from "@/app/actions/support";
import type { LangCode } from "@/types/message";
import type { SupportMessage } from "@/types/support";

const LOCALE_TO_COLUMN: Record<LangCode, keyof SupportMessage> = {
  ko: "content_ko",
  en: "content_en",
  ja: "content_ja",
  "zh-CN": "content_zh_cn",
  "zh-TW": "content_zh_tw",
};

interface SupportMessageBubbleProps {
  message: SupportMessage;
  /** The viewer's locale — picks which content_<locale> column to display. */
  viewerLocale: LangCode;
  /** True if this message was sent by the viewer themselves. */
  isMine: boolean;
}

function pickContent(message: SupportMessage, locale: LangCode): string {
  const column = LOCALE_TO_COLUMN[locale];
  const localized = message[column] as string | null | undefined;
  return localized || message.content || "";
}

export function SupportMessageBubble({
  message,
  viewerLocale,
  isMine,
}: SupportMessageBubbleProps) {
  const t = useTranslations("support.chat");

  // Sidebar-style "system" capsule is reserved for explicit announcements
  // (message_type === 'system'). LOCARORA team auto-messages (sender_type
  // 'system' but message_type 'text') render as normal admin bubbles.
  const isSystem = message.message_type === "system";
  const localizedContent = pickContent(message, viewerLocale);
  const originalLang = message.original_lang_code as LangCode | null;
  const localizedColumn = LOCALE_TO_COLUMN[viewerLocale];
  const localizedColumnFilled = Boolean(
    message[localizedColumn] as string | null | undefined,
  );

  // Lazy translation populates the cache when the viewer clicks
  // "번역"; thereafter the toggle below switches between the
  // viewer-language body and the source.
  const [translation, setTranslation] = useState<string | null>(null);
  const [translating, setTranslating] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);

  const viewerLocaleText = translation ?? localizedContent;
  // Own bubbles render the literal source, unless the viewer explicitly
  // asked for a translation of their own message.
  const ownText =
    translation ?? message.original_content ?? message.content ?? "";

  // `original_lang_code` allowed NULL — a lot of legacy rows ship
  // without it but the original_content is still meaningful. Label
  // below drops the "(lang)" hint when the code isn't known.
  const canToggle =
    !isMine &&
    originalLang !== viewerLocale &&
    Boolean(message.original_content) &&
    viewerLocaleText !== message.original_content;

  const renderedContent = isMine
    ? ownText
    : showOriginal && message.original_content
    ? message.original_content
    : viewerLocaleText;

  const handleTranslate = async () => {
    if (translating) return;
    setTranslating(true);
    try {
      const { data } = await translateSupportMessage(message.id, viewerLocale);
      if (data) setTranslation(data);
    } finally {
      setTranslating(false);
    }
  };

  if (isSystem) {
    return (
      <div className="my-2 flex justify-center">
        <span className="rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-600">
          {localizedContent}
        </span>
      </div>
    );
  }

  // Mirror partner 1:1 MessageBubble exactly: bubble + a narrow
  // metadata column beside it (time / translate / toggle), instead of
  // a row of metadata stacked below the bubble. Keeps all chat surfaces
  // visually identical.
  return (
    <div
      className={cn(
        "flex items-end gap-1.5",
        isMine ? "flex-row-reverse" : "",
      )}
    >
      <div
        className={cn(
          "max-w-[85%] sm:max-w-[70%] whitespace-pre-wrap wrap-break-word rounded-2xl px-3 sm:px-4 py-2 text-sm leading-relaxed",
          isMine
            ? "bg-orange-200 text-gray-900 rounded-br-none"
            : "bg-gray-100 text-gray-900 rounded-bl-none",
        )}
      >
        {renderedContent}
      </div>

      <div
        className={cn(
          "flex flex-col gap-0.5 shrink-0 pb-0.5",
          isMine ? "items-end" : "items-start",
        )}
      >
        <span className="text-xs text-gray-400">
          {new Date(message.created_at).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
        {/* Translate button — shown when no viewer-locale cache exists
            and the source isn't already in the viewer's language. Applies
            to own/admin/system messages too (mirror of the partner 1:1
            MessageBubble) so e.g. an operator message without a cached
            viewer-locale version can still be translated on demand. */}
        {!translation &&
          !localizedColumnFilled &&
          originalLang !== viewerLocale && (
            <button
              type="button"
              disabled={translating}
              onClick={handleTranslate}
              className="inline-flex items-center gap-1 text-xs text-gray-700 hover:text-gray-900 whitespace-nowrap disabled:opacity-50"
            >
              <Repeat className="h-3 w-3" aria-hidden="true" />
              {translating ? t("translating") : t("translate")}
            </button>
          )}
        {/* Source toggle — visible when both versions exist. */}
        {canToggle && (
          <button
            type="button"
            onClick={() => setShowOriginal((v) => !v)}
            className="text-xs text-gray-700 hover:text-gray-900 whitespace-nowrap"
          >
            {showOriginal ? t("hideOriginal") : t("showOriginal")}
          </button>
        )}
      </div>
    </div>
  );
}
